/**
 * Minimal async data hook.
 *
 * PLAN.md §11 pencilled in TanStack Query for this point. It was not needed: the app has a handful
 * of independent fetches and one polling loop, all of which this covers in ~40 lines with no extra
 * dependency. Worth revisiting if cache invalidation or optimistic updates get more complex.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  setData: (value: T | null) => void;
}

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Guards against a resolved promise from a previous run overwriting newer state.
  const runId = useRef(0);

  useEffect(() => {
    const current = ++runId.current;
    setLoading(true);
    setError(null);

    loader()
      .then((value) => {
        if (runId.current === current) setData(value);
      })
      .catch((caught: unknown) => {
        if (runId.current === current) {
          setError(caught instanceof Error ? caught.message : 'Something went wrong');
        }
      })
      .finally(() => {
        if (runId.current === current) setLoading(false);
      });

    return () => {
      // Invalidate in-flight work for the unmounted/re-run effect.
      runId.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, error, loading, reload, setData };
}

/** Polls `loader` on an interval until `shouldStop` returns true. */
export function usePolling<T>(
  loader: () => Promise<T>,
  shouldStop: (value: T) => boolean,
  intervalMs: number,
  active: boolean,
): { data: T | null; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stopped = useRef(false);

  useEffect(() => {
    if (!active) return;
    stopped.current = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const value = await loader();
        if (stopped.current) return;
        setData(value);
        setError(null);
        if (shouldStop(value)) return;
      } catch (caught) {
        if (stopped.current) return;
        setError(caught instanceof Error ? caught.message : 'Polling failed');
      }
      if (!stopped.current) timer = setTimeout(tick, intervalMs);
    };

    void tick();

    return () => {
      stopped.current = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, intervalMs]);

  return { data, error };
}
